import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Divider,
} from "@mui/material";
import { Link } from "react-router-dom";
import { InitialNameAvatar } from "../../../../components/images/initial-name-avatar";
import { RENDERER_TYPES } from "../../../../enums";
import {
  convertToFullName,
  getConvertedExternalUrl,
  numberFormat,
} from "../../../../services/utils";
import { useStyles } from "./styles";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import EmailIcon from "@mui/icons-material/Email";
import BusinessIcon from "@mui/icons-material/Business";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import WorkIcon from "@mui/icons-material/Work";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PeopleIcon from "@mui/icons-material/People";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CategoryIcon from "@mui/icons-material/Category";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import IntrestedBlock, {
  getPriorityColor,
} from "../../add-or-update/priorities/intrested-block";
import Environment from "./environment";
import ProductBlock from "./product-block";
import { styled } from "@mui/material/styles";
import CopytoclipboardIcon from "../../../../components/CopytoclipboardIcon";

const TargetInfoCard = styled(Card)(({ theme }) => ({
  borderRadius: "8px",
  border: "1px solid #E5E7EB",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
}));

function AmbassadorInfoDetails({ data, fieldList }) {
  const classes = useStyles();

  const getFieldIcon = (field) => {
    const iconMap = {
      email: <EmailIcon />,
      secondaryMembers: <EmailIcon />,
      linkedIn: <LinkedInIcon />,
      title: <WorkIcon />,
      company: <CorporateFareIcon />,
      region: <CategoryIcon />,
      no_of_emp: <PeopleIcon />,
      industry: <BusinessIcon />,
      city: <LocationOnIcon />,
      timezone: <ScheduleIcon />,
      experience: <TrendingUpIcon />,
      investment_budget: <AttachMoneyIcon />,
      round_table_package_12_rate: <AttachMoneyIcon />,
      previous_company: <CorporateFareIcon />,
      topics: <CategoryIcon />,
    };
    return iconMap[field] || <WorkIcon />;
  };

  const renderValue = (detail) => {
    const fieldValue = data[detail.field];

    // Check if value is empty, null, or undefined
    const isEmpty =
      !fieldValue ||
      (Array.isArray(fieldValue) && fieldValue.length === 0) ||
      (typeof fieldValue === "string" && fieldValue.trim() === "");

    if (isEmpty) {
      return <Typography className={classes.emptyValue}>-</Typography>;
    }

    if (detail.field === "email") {
      return (
        <Typography
          component="span"
          sx={{
            fontSize: "0.875rem",
            color: "text.secondary",
            wordBreak: "break-word",
          }}
        >
          {fieldValue}
          <CopytoclipboardIcon
            item={fieldValue}
            sx={{ p: 0, ml: 1.5, verticalAlign: "middle", display: "inline-flex" }}
            color="#070639"
            iconSize={16}
          />
        </Typography>
      );
    }

    switch (detail.renderer) {
      case RENDERER_TYPES.NUMBER:
        return `${numberFormat(data[detail.field])}`;
      case RENDERER_TYPES.MULTIPLE_TEXT_RENDERER: {
        const multipleValues = data?.[detail?.field];
        if (!multipleValues || multipleValues.length === 0) {
          return <Typography className={classes.emptyValue}>-</Typography>;
        }
        return multipleValues.join(", ");
      }
      case RENDERER_TYPES.TIMEZONE:
        return data.timezone?.value ? (
          `${data.timezone.value}`
        ) : (
          <Typography className={classes.emptyValue}>-</Typography>
        );
      case RENDERER_TYPES.CITY_STATE:
        return data.city && data.state ? (
          `${data.city}, ${data.state}`
        ) : (
          <Typography className={classes.emptyValue}>-</Typography>
        );
      case RENDERER_TYPES.AMOUNT:
        return `${numberFormat(data[detail.field], "USD")}`;
      case RENDERER_TYPES.CHIP_LIST:
        if (detail.field === "topics") {
          const topics = data[detail.field];
          if (!topics || topics.length === 0) {
            return <Typography className={classes.emptyValue}>-</Typography>;
          }
          return topics.map((item, i) => (
            <Chip
              key={`topics-${i}`}
              label={item?.label}
              size="small"
              color="primary"
              variant="outlined"
              className={classes.chip}
              style={{
                backgroundColor: getPriorityColor(item?.priority),
                color: "white",
                marginRight: 3,
              }}
            />
          ));
        }

        const chipValues = data[detail.field];
        if (!chipValues || chipValues.trim() === "") {
          return <Typography className={classes.emptyValue}>-</Typography>;
        }
        return chipValues
          .split(",")
          .map((item, i) => (
            <Chip
              key={`${detail.field}-${i}`}
              label={item.trim()}
              size="small"
              color="primary"
              variant="outlined"
              className={classes.chip}
            />
          ));
      case RENDERER_TYPES.INVESTMENT_BUDGET:
        return data[detail.field] || 0;
      case RENDERER_TYPES.LINKEDIN_URL_WITH_ICON:
        return (
          <Link
            to={{ pathname: getConvertedExternalUrl(data[detail.field]) }}
            target="_blank"
            title="Linkedin URL"
            className={classes.linkedinLink}
          >
             <Box className={classes.cardValue}>View Profile</Box>
            <OpenInNewIcon className={classes.linkedinIcon} fontSize="small" />
          </Link>
        );
      default:
        return data[detail.field];
    }
  };

  // Check if field should be in the right sidebar
  const isRightSideField = (field) => {
    return ["topics"].includes(field);
  };

  // Check if field should be full width
  const isFullWidthField = (field) => {
    return ["previous_company"].includes(field);
  };

  const InfoCard = ({ detail, value }) => (
    <Card className={classes.infoCard}>
      <CardContent className={classes.cardContent}>
        <Box className={classes.cardHeader}>
          <Box className={classes.cardIcon}>{getFieldIcon(detail.field)}</Box>
          <Typography variant="subtitle2" className={classes.cardLabel}>
            {detail.label}
          </Typography>
        </Box>
        <Box className={classes.cardValue}>{value}</Box>
      </CardContent>
    </Card>
  );

  return (
    <>
      {/* Profile Header */}
      <Box className={classes.profileHeader}>
        <InitialNameAvatar
          style={{
            bgcolor: "#070639",
            width: 60,
            height: 60,
            fontSize: 32,
          }}
          textStyle={{
            fontSize: 20,
          }}
          name={data.firstName}
          text={convertToFullName(data.firstName, data.lastName)}
          image
          src={data.photo || data.picture}
        />
      </Box>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column - Information Cards (including full-width ones) */}
        <Grid item xs={12} lg={8}>
          <Grid container spacing={2}>
            {fieldList
              .filter((detail) => !isRightSideField(detail.field))
              .map((detail, index) => (
                <Grid
                  item
                  xs={12}
                  sm={isFullWidthField(detail.field) ? 12 : 6}
                  md={isFullWidthField(detail.field) ? 12 : 4}
                  key={index}
                >
                  <InfoCard detail={detail} value={renderValue(detail)} />
                </Grid>
              ))}
          </Grid>
        </Grid>

        {/* Right Column - Special Sections */}
        <Grid item xs={12} lg={4}>
          <TargetInfoCard>
            <CardContent sx={{ p: 0 }}>
              <Box
                sx={{
                  p: 1,
                  backgroundColor: "#F8FAFC",
                  borderBottom: "1px solid #E5E7EB",
                  borderRadius: "8px 8px 0 0",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#374151",
                    margin: 0,
                  }}
                >
                  Other Information
                </Typography>
              </Box>
              <Box p={2}>
                {/* Areas of Expertise Section */}
                {fieldList
                  .filter((detail) => isRightSideField(detail.field))
                  .map((detail, index) => (
                    <Box key={index} className={classes.specialSection}>
                      <Typography
                        variant="subtitle2"
                        className={classes.specialSectionTitle}
                      >
                        {detail.label}
                      </Typography>
                      <Box className={classes.specialSectionContent}>
                        {renderValue(detail)}
                      </Box>
                    </Box>
                  ))}

                {/* Environment Section */}
                <Box className={classes.specialSection}>
                  <Typography
                    variant="subtitle2"
                    className={classes.specialSectionTitle}
                  >
                    Environment
                  </Typography>
                  <Box className={classes.specialSectionContent}>
                    {data?.environment ? (
                      <Environment data={data} />
                    ) : (
                      <Typography className={classes.emptyValue}>
                        No environment data available
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Technology Stack Section */}
                <Box className={classes.specialSection}>
                  <Typography
                    variant="subtitle2"
                    className={classes.specialSectionTitle}
                  >
                    Cybersecurity Technology Stack
                  </Typography>
                  <Box className={classes.scrollableContent}>
                    {data?.technologies && data.technologies.length > 0 ? (
                      <Grid container spacing={2}>
                        {data.technologies.map((item, index) => (
                          <Grid item xs={12} key={`technology-stack-${index}`}>
                            <ProductBlock
                              data={item}
                              editable={false}
                              onEditClick={() => {}}
                              onDeleteClick={() => {}}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Typography className={classes.emptyValue}>
                        No technology stack data available
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </TargetInfoCard>
        </Grid>
      </Grid>

      {/* Full Width Sections removed: full-width fields are rendered as cards in the left grid */}
    </>
  );
}

export { AmbassadorInfoDetails };
